import { X, Building2 } from 'lucide-react'
import { useState } from 'react'

import { toast } from '@/shared/lib/toast'

import { FormField } from './FormField'

export function AddVendorModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialVendor = null,
}) {
  if (!isOpen) return null

  const formKey = initialVendor?._id || 'new-vendor'

  return (
    <VendorModalForm
      key={formKey}
      onClose={onClose}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      initialVendor={initialVendor}
    />
  )
}

function VendorModalForm({ onClose, onSubmit, isSubmitting, initialVendor }) {
  const [form, setForm] = useState(() => ({
    vendorName: initialVendor?.vendorName || '',
    email: initialVendor?.email || '',
    gstNumber: initialVendor?.gstNumber || '',
    phoneNumber: initialVendor?.phoneNumber || '',
    address: initialVendor?.address || '',
  }))

  const isEditing = Boolean(initialVendor)

  const handleChange = (e) => {
    const { name, value } = e.target
    let sanitized = value
    if (name === 'vendorName') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.,&'()/-]/g, '')
    } else if (name === 'address') {
      sanitized = value.replace(/[^a-zA-Z0-9\s.,#&'()/-]/g, '')
    } else if (name === 'phoneNumber') {
      sanitized = value.replace(/\D/g, '').slice(0, 10)
    } else if (name === 'gstNumber') {
      sanitized = value
        .replace(/[^a-zA-Z0-9]/g, '')
        .slice(0, 15)
        .toUpperCase()
    }
    setForm((prev) => ({ ...prev, [name]: sanitized }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!form.vendorName?.trim()) {
      toast.error('Vendor Name is required')
      return
    }
    if (!/^[a-zA-Z0-9\s.,&'()/-]+$/.test(form.vendorName.trim())) {
      toast.error('Vendor Name must accept alphanumeric characters')
      return
    }
    if (!form.email?.trim()) {
      toast.error('Email is required')
      return
    }
    if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
        form.email.trim(),
      )
    ) {
      toast.error('Please enter a valid email address')
      return
    }
    if (!form.phoneNumber?.trim()) {
      toast.error('Phone number is required')
      return
    }
    const cleanPhone = form.phoneNumber.trim().replace(/[\s-]/g, '')
    if (!/^\d{10}$/.test(cleanPhone)) {
      toast.error(
        'Phone number must accept only numbers and be exactly 10 digits',
      )
      return
    }
    if (!form.address?.trim()) {
      toast.error('Address is required')
      return
    }
    if (!/^[a-zA-Z0-9\s.,#&'()/-]+$/.test(form.address.trim())) {
      toast.error('Address must accept alphanumeric characters')
      return
    }
    const cleanGst = form.gstNumber?.trim().toUpperCase() || ''
    if (cleanGst && !/^[a-zA-Z0-9]{15}$/.test(cleanGst)) {
      toast.error(
        'GST must accept alphanumeric characters and be exactly 15 characters',
      )
      return
    }

    const success = await onSubmit({
      ...form,
      phoneNumber: cleanPhone,
      gstNumber: cleanGst,
    })

    if (success) {
      onClose()
    }
  }

  const modalTitle = isEditing ? 'Edit Vendor' : 'Add Vendor'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="vendor-modal-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4.5">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-2xl bg-[#080d25] text-white">
              <Building2 className="size-5" />
            </div>

            <div>
              <h2
                id="vendor-modal-title"
                className="text-lg font-bold tracking-tight text-[#080d25]"
              >
                {modalTitle}
              </h2>
              <p className="text-xs text-slate-500">
                Enter vendor contact, address, and GST identification.
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            aria-label="Close modal"
            className="flex size-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="size-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              label="Vendor Name"
              name="vendorName"
              value={form.vendorName}
              onChange={handleChange}
              placeholder="e.g. Precision Engineering Ltd."
              required
            />

            <FormField
              label="Email Address"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="vendor@example.com"
              required
            />

            <FormField
              label="GST Number (Optional)"
              name="gstNumber"
              maxLength={15}
              value={form.gstNumber}
              onChange={handleChange}
              placeholder="e.g. 27AABCP1234F1Z5 (Optional)"
            />

            <FormField
              label="Phone Number"
              name="phoneNumber"
              type="tel"
              maxLength={10}
              value={form.phoneNumber}
              onChange={handleChange}
              placeholder="Enter 10-digit phone number"
              required
            />

            <div className="sm:col-span-2">
              <label
                htmlFor="vendor-address"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-700"
              >
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                id="vendor-address"
                name="address"
                value={form.address}
                onChange={handleChange}
                required
                rows={3}
                placeholder="Full registered address, plot/road, city, pincode"
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-xs outline-none transition placeholder:text-slate-400 focus:border-[#080d25] focus:ring-2 focus:ring-[#080d25]/10"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-[#080d25] px-6 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-[#17203e] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? isEditing
                  ? 'Updating...'
                  : 'Saving...'
                : isEditing
                  ? 'Update Vendor'
                  : 'Save Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
